import { useEffect } from 'react';
import { Paper, PaperProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RemindingTone, useInvoiceQuery, useUserQuery } from '../../api/gql';
import { FullSizeLoader } from '../../components/FullSizeLoader';
import { notify } from '../../utils/notification';
import { fromCentesimal } from '../../utils/transform';
import { InvoiceFormProvider, InvoiceFormProviderProps } from '../forms/invoice/form-provider/InvoiceFormProvider';

type Props = {
  invoiceId: string;
  invoiceFormProps: InvoiceFormProviderProps;
} & PaperProps;

export const InvoiceFormModule = ({ invoiceId, invoiceFormProps, sx, ...paperProps }: Props) => {
  const { t } = useTranslation('page-new-invoice');

  const { isLoading, data: invoiceData, error } = useInvoiceQuery({ id: invoiceId });

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({
        title: t('error.query.title'),
        message: t('error.query.text'),
      });
    }
  }, [error, t]);

  const { data: userData } = useUserQuery();

  return (
    <Paper
      variant={'outlined'}
      sx={{
        position: 'relative',
        flex: 1,
        p: {
          xs: 2,
          md: 4,
        },
        borderRadius: 0,
        maxWidth: '250mm',
        minHeight: '250mm',
        ...sx,
      }}
      {...paperProps}
    >
      {invoiceData && userData ? (
        <InvoiceFormProvider
          {...invoiceFormProps}
          defaultValues={{
            ...invoiceFormProps.defaultValues,
            ...invoiceData?.invoice,
            amount: invoiceData?.invoice.amount ? fromCentesimal(invoiceData.invoice.amount) : undefined,
            amountDue: invoiceData?.invoice.amountDue ? fromCentesimal(invoiceData.invoice.amountDue) : undefined,
            currency: invoiceData?.invoice.currency?.toLowerCase() ?? 'czk',
            customer: {
              name: invoiceData?.invoice.customer?.name,
              address: invoiceData?.invoice.customer?.address,
              email: invoiceData?.invoice.customer?.email,
              phoneNumber: invoiceData?.invoice.customer?.phoneNumber,
              taxId: invoiceData?.invoice.customer?.taxId,
              vatId: invoiceData?.invoice.customer?.vatId,
              remindingTone:
                invoiceData?.invoice.customer?.remindingTone ??
                userData?.user.defaultRemindingTone ??
                RemindingTone.Neutral,
            },
            items:
              invoiceData?.invoice.items.map((item) => ({
                ...item,
                priceSum: fromCentesimal(item.priceSum),
                pricePerUnit: fromCentesimal(item.pricePerUnit),
                vatRate: item.vatRate ?? undefined,
                priceWithoutVat: item.priceWithoutVat ? fromCentesimal(item.priceWithoutVat) : undefined,
              })) ?? [],
          }}
        />
      ) : null}

      {isLoading ? <FullSizeLoader /> : null}
    </Paper>
  );
};
